import React, { useMemo } from "react";
import { Grid } from "@mui/material";
import {
  AccessTimeFilledRounded,
  Assignment,
  BlockRounded,
  CheckRounded,
  DeleteRounded,
  EditRounded,
  GridViewRounded,
  NotificationsRounded,
  SearchRounded,
  SendRounded,
  SupportAgentRounded,
  WarningRounded,
} from "@mui/icons-material";
import TipCard, { TipCardBody } from "./TipCard";
import LocationApprovalWorkflow, { WorkflowStep } from "./LocationApprovalWorkflow";

/**
 * Status reference grid + informational tip cards shown in the collapsible
 * InfoBanner body. Tiles and tips share the same Active → Pending → Draft →
 * Deactivated ordering. Tip cards lay out in a 2-column grid on sm+ screens,
 * single column on xs.
 */
const GettingStartedWithLocations = () => {
  const workflowSteps: WorkflowStep[] = useMemo(() => [
    {
      id: "active",
      variant: "active",
      icon: <CheckRounded fontSize="small" />,
      label: "Active",
      description: "Approved and live. Buyers can find and book your space in search results.",
      actions: [
        { id: "edit", icon: <EditRounded fontSize="small" />, text: "Edit location details" },
        { id: "deactivate", icon: <BlockRounded fontSize="small" />, text: "Deactivate to take offline" },
      ],
    },
    {
      id: "pending",
      variant: "pending",
      icon: <AccessTimeFilledRounded fontSize="small" />,
      label: "Pending",
      description: "Submitted for SecurSpace admin review. Not yet visible to buyers.",
      actions: [
        { id: "edit", icon: <EditRounded fontSize="small" />, text: "Edit while under review" },
        { id: "notify", icon: <NotificationsRounded fontSize="small" />, text: "You'll be notified on approval" },
      ],
    },
    {
      id: "draft",
      variant: "draft",
      icon: <EditRounded fontSize="small" />,
      label: "Draft",
      description: "Created but not yet submitted. Complete all required fields to go live.",
      actions: [
        { id: "edit", icon: <EditRounded fontSize="small" />, text: "Continue editing" },
        { id: "submit", icon: <SendRounded fontSize="small" />, text: "Submit for review when ready" },
      ],
    },
    {
      id: "deactivated",
      variant: "deactivated",
      icon: <BlockRounded fontSize="small" />,
      label: "Deactivated",
      description: "Taken offline. No longer visible to buyers or searchable.",
      actions: [
        { id: "reactivate", icon: <SupportAgentRounded fontSize="small" />, text: "Contact support to reactivate" },
        { id: "delete", icon: <DeleteRounded fontSize="small" />, text: "Delete if no longer needed" },
      ],
    },
  ], []);

  return (
    <Grid container spacing={2}>

      {/* Status reference grid — full width */}
      <Grid item xs={12}>
        <LocationApprovalWorkflow
          headerIcon={<GridViewRounded fontSize="inherit" />}
          steps={workflowSteps}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TipCard
          variant="success"
          icon={<SearchRounded fontSize="small" />}
          title="How buyers find your Active location"
        >
          <TipCardBody component="div">
            Only <strong>Active</strong> locations appear in buyer search results. Once approved
            by a SecurSpace admin, your facility is immediately discoverable by customers searching
            for space in your area.
          </TipCardBody>
        </TipCard>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TipCard
          variant="warn"
          icon={<AccessTimeFilledRounded fontSize="small" />}
          title="What happens while Pending"
        >
          <TipCardBody component="div">
            Your location is under SecurSpace admin review and <strong>not yet visible to
            buyers</strong>. You can still edit details while it's pending. You'll receive a
            notification once it is approved and goes live.
          </TipCardBody>
        </TipCard>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TipCard
          variant="default"
          icon={<Assignment fontSize="small" />}
          title="Complete required fields to submit a Draft"
        >
          <TipCardBody component="div">
            Your location needs a{" "}
            <strong>name, address, pricing, equipment types, and at least one feature</strong>{" "}
            before it can be submitted for review. Check the progress bar on each Draft card to
            see what's missing.
          </TipCardBody>
        </TipCard>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TipCard
          variant="error"
          icon={<WarningRounded fontSize="small" />}
          title="Deactivating a location with active bookings"
        >
          <TipCardBody component="div">
            If your location has <strong>active bookings</strong>, you must cancel or close them
            before deactivation. Contact{" "}
            <a href="mailto:support@secur.space">
              SecurSpace support
            </a>{" "}
            if you need assistance processing outstanding bookings.
          </TipCardBody>
        </TipCard>
      </Grid>

    </Grid>
  );
};

export default GettingStartedWithLocations;
